import { redactPrivate } from '@shared/contracts/problem-reports'
import { ApiRequestError } from './api'

/** For anything that failed on our side (5xx, offline, timeouts). */
export const OUR_FAULT =
  'Something went wrong on our side. Your information isn’t lost — try again.'

/** Server defaults that say nothing useful; a specific server message is shown as written. */
const GENERIC = new Set([
  'Sign in required',
  'You do not have permission to do that',
  'Too many requests',
  'Too many requests; please slow down',
  'Something went wrong',
  'Route not found',
  'Request failed',
  'Bad request',
  'Conflict',
  'Validation failed',
  'Cross-site request blocked',
])

export interface ErrorInfo {
  /** Plain-language message for people. */
  message: string
  /** HTTP status when the API answered; null for network failures and app errors. */
  status: number | null
  /** On us (server error, network, app bug): offer a retry and point to "Report a problem". */
  ours: boolean
  /** Signed out: show a sign-in link back to this page. */
  signedOut: boolean
  requestId: string | null
  /** The technical text, for problem reports. */
  detail: string
  /** 422: what was wrong with which answer (`email: Invalid email`). */
  fields?: string[]
}

/** Validation issues (`[{ path, message }]`) as short lines, at most five. */
function fieldErrors(details: unknown): string[] {
  if (!Array.isArray(details)) return []
  return details
    .filter((d): d is { path?: unknown[]; message: string } => typeof d?.message === 'string')
    .slice(0, 5)
    .map((d) => {
      const path = Array.isArray(d.path) ? d.path.join('.') : ''
      return path ? `${path}: ${d.message}` : d.message
    })
}

const NETWORK =
  /Failed to fetch|NetworkError|Network request failed|Load failed|fetch failed|timed? ?out/i

/** A lazily loaded part of the app is gone (usually: a new version was deployed). */
export function isChunkLoadError(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? '')
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError|Loading chunk \S+ failed/i.test(
    text,
  )
}

export function describeError(error: unknown): ErrorInfo {
  const base = { signedOut: false, requestId: null as string | null }
  if (error instanceof ApiRequestError) {
    const s = error.status
    const server = error.message
    const generic = GENERIC.has(server) || error.code === 'http_error'
    const detail = `${s} ${error.code}: ${server}`
    const info = (message: string, ours = false) => ({
      ...base,
      message,
      status: s,
      ours,
      requestId: error.requestId,
      detail,
    })
    if (s === 401)
      return { ...info('You’ve been signed out. Sign in again to carry on.'), signedOut: true }
    if (s === 403) return info(generic ? 'You don’t have access to this.' : server)
    // Not-found messages from the API name records by id; they never help people.
    if (s === 404)
      return info(generic || /not found$/i.test(server) ? 'We couldn’t find that.' : server)
    if (s === 422)
      return {
        ...info(generic ? 'Some answers need another look.' : server),
        fields: fieldErrors(error.details),
      }
    if (s === 429) return info('Too many tries — wait a minute and try again.')
    if (s >= 500) return info(OUR_FAULT, true)
    // 400, 402, 409…: written for people.
    return info(generic ? 'That didn’t work. Please check and try again.' : server)
  }
  if (error instanceof Error) {
    const detail = `${error.name}: ${error.message}`
    if (
      isChunkLoadError(error) ||
      error.name === 'AbortError' ||
      error.name === 'TimeoutError' ||
      (error.name === 'TypeError' && NETWORK.test(error.message))
    )
      return { ...base, message: OUR_FAULT, status: null, ours: true, detail }
    return { ...base, message: error.message, status: null, ours: true, detail }
  }
  const text = String(error ?? 'Something went wrong')
  return { ...base, message: text, status: null, ours: true, detail: text }
}

/** This page's address without private tokens, for problem reports. */
export function currentPageForReport(): string {
  return redactPrivate(window.location.href)
}

/** The build's entry file name (it carries a content hash), or `dev`. */
export function appVersion(): string {
  const entry = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
  return entry?.src.split('/').pop()?.slice(0, 100) ?? 'dev'
}

const RELOAD_KEY = 'bp:new-version-reload-at'
/** One automatic reload per this long, so a page that keeps failing can't reload forever. */
const RELOAD_GUARD_MS = 5 * 60 * 1000

/** Whether a failed chunk load may reload the page (it hasn't just done so). */
export function canReloadForNewVersion(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0)
    return !(Date.now() - last < RELOAD_GUARD_MS)
  } catch {
    // No session storage: never reload automatically (we couldn't stop a loop).
    return false
  }
}

/** Reloads once to pick up a new version. Returns false (and does nothing) if it just did. */
export function reloadForNewVersion(reload: () => void = () => window.location.reload()): boolean {
  if (!canReloadForNewVersion()) return false
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    return false
  }
  reload()
  return true
}
